/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-empty-function */
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { Documents } from '@vsolv/packages/documents/domain';
import { Note } from '@vsolv/packages/notes/domain';
import { Payment } from '@vsolv/packages/payments/domain';
import { debouncedSearchLatest } from '@vsolv/utils/rxjs-operators';
import { InfoCardConfig } from '@vsolv/vectors-ui/info-card';
import { Link } from '@wsphere/links/domain';
import { LinksWebService } from '@wsphere/links/web';
import { Claim } from '@wsphere/warranties/domain';
import { BehaviorSubject, Observable, ReplaySubject, combineLatest, forkJoin, merge, of } from 'rxjs';
import { catchError, map, shareReplay, startWith, switchMap, take, tap } from 'rxjs/operators';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-claim-link-picker',
  templateUrl: './link-picker.component.html',
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: ClaimLinkPickerComponent,
    },
  ],
})
export class ClaimLinkPickerComponent implements ControlValueAccessor, OnDestroy {
  constructor(private linkSvc: LinksWebService) {}

  @PropertyListener('value') value$ = new ReplaySubject<Link.FormLink[] | null>();
  @Input() value: Link.FormLink[] | null = null;

  @PropertyListener('claim') claim$ = new ReplaySubject<Claim.Model>();
  @Input() claim: Claim.Model | null = null;

  @PropertyListener('id') id$ = new ReplaySubject<string>();
  @Input() id: string | null = null;

  @Input() asButton = false;
  @Input() disabled = false;
  @Input() placeholder = 'Add Link';

  @Output() valueChanges = new EventEmitter<Link.FormLink[] | null>();
  @Output() linksAndConfigs = new EventEmitter<{ link: Link.FormLink; config: InfoCardConfig }[] | undefined>();

  touched = false;
  overlayOpen = false;
  selectedGroup: { type: Link.ObjectType; objects: unknown[] } | null = null;

  search$ = new BehaviorSubject<string>('');
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  readonly loading$ = this.loadingSubject.asObservable();

  refresh$ = new BehaviorSubject(null);

  private readonly searchTriggers$ = merge(
    this.search$,
    this.claim$.pipe(map(() => this.search$.value)),
    this.id$.pipe(map(() => this.search$.value))
  );

  typesAndObjects$: Observable<{ type: Link.ObjectType; objects: unknown[] }[]> = this.searchTriggers$.pipe(
    debouncedSearchLatest(
      (searchTerm: string) =>
        combineLatest([this.claim$, this.id$.pipe(startWith(null))]).pipe(
          take(1),
          switchMap(([claim]) => {
            if (!claim) return of([]);

            const filteredItems = this.getItems(claim, searchTerm);
            this.loadingSubject.next(true);

            return forkJoin({
              items: of(filteredItems),
              notes: this.getNotes$(claim, searchTerm),
              documents: this.getDocuments$(claim, searchTerm),
              payments: this.getPayments$(claim, searchTerm),
            }).pipe(
              map(({ items, notes, documents, payments }) => {
                const typesAndObjects: { type: Link.ObjectType; objects: unknown[] }[] = [];

                if (items?.length) typesAndObjects.push({ type: Link.ObjectType.CLAIM_ITEM, objects: items });
                if (notes?.length) typesAndObjects.push({ type: Link.ObjectType.NOTE, objects: notes });
                if (documents?.length) typesAndObjects.push({ type: Link.ObjectType.DOCUMENT, objects: documents });
                if (payments?.length) typesAndObjects.push({ type: Link.ObjectType.PAYMENT, objects: payments });

                return typesAndObjects;
              }),
              tap(() => this.loadingSubject.next(false)),
              catchError(() => {
                this.loadingSubject.next(false);
                return of([] as { type: Link.ObjectType; objects: unknown[] }[]);
              })
            );
          })
        ),
      { debounceMs: 300 }
    ),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  linksAndConfigs$: Observable<{ link: Link.FormLink; config: InfoCardConfig }[] | undefined> = combineLatest([
    this.value$,
    this.refresh$,
  ]).pipe(
    map(([value]) =>
      value?.filter(link => link.type !== Link.ObjectType.ACTIVITY && (link.object as any)?.id !== this.claim?.id)
    ),
    map(value =>
      value?.map(link => ({
        link: link,
        config: {
          icon: Link.getLinkIcon(link.type),
          title: Link.getObjectTitle(link.object, link.type),
          subtitle: Link.getObjectSubtitle(link.object, link.type),
        },
      }))
    ),
    tap(linksAndConfigs => this.linksAndConfigs.emit(linksAndConfigs))
  );

  onChange = (_value: Link.FormLink[] | null) => {};
  onTouched = () => {};

  selectValueChanged(object: unknown, type: Link.ObjectType) {
    this.markAsTouched();

    if (this.value?.map(item => (item.object as any)?.id).includes((object as any)?.id)) {
      this.value = this.value.filter(item => (item.object as any)?.id !== (object as any)?.id);
    } else {
      if (!this.value?.length) this.value = [];
      this.value?.push({ object, type });
    }

    this.onChange(this.value);
    this.valueChanges.next(this.value);

    this.refresh$.next(null);
  }

  writeValue(value: Link.FormLink[] | null): void {
    if (value?.length) {
      this.value = value;
    } else this.value = null;
  }

  registerOnChange(onChange: (_value: Link.FormLink[] | null) => {}): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => {}): void {
    this.onTouched = onTouched;
  }

  setDisabledState?(disabled: boolean): void {
    this.disabled = disabled;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  open() {
    this.overlayOpen = true;
  }

  close() {
    this.overlayOpen = false;
    this.selectedGroup = null;
  }

  toggle() {
    this.overlayOpen = !this.overlayOpen;
  }

  selectedCheck(object: unknown) {
    return this.value?.map(item => (item.object as any)?.id).includes((object as any)?.id);
  }

  getObjectTitle(object: unknown, type: Link.ObjectType) {
    switch (type) {
      case Link.ObjectType.CLAIM_ITEM:
        return (object as Claim.ClaimItem.Model).coverage?.title || 'Other';
      case Link.ObjectType.NOTE:
        return (
          (object as Note.Model)?.title ||
          (object as Note.Model)?.content.replace(/<[^>]*>/g, ' ').replace('&nbsp;', '') ||
          'Note'
        );
      case Link.ObjectType.DOCUMENT:
        return (object as Documents.Model)?.title || (object as Documents.Model)?.description || 'Document';
      case Link.ObjectType.PAYMENT:
        return (object as Payment.Model)?.title || (object as Payment.Model)?.description || 'Payment';
      default:
        return type;
    }
  }

  getItemTheme(item?: Claim.ClaimItem.Model) {
    return item ? Claim.ClaimItem.getTheme(item.status) : 'default';
  }

  private getItems(claim: Claim.Model, search: string) {
    return claim.items?.filter(
      item =>
        !item.deleted &&
        item.id !== this.id &&
        (item.coverage?.title.toLowerCase().includes(search.toLowerCase()) ||
          (!item.coverage?.title && 'other'.includes(search.toLowerCase())))
    );
  }

  private getNotes$(claim: Claim.Model, search: string) {
    return this.linkSvc
      .list$(
        { id: claim.id, objectType: Link.ObjectType.CLAIM },
        {
          owner: { id: claim.id, objectType: Link.ObjectType.CLAIM },
          linkType: Link.ObjectType.NOTE,
          limit: 100,
        }
      )
      .pipe(
        map(notes =>
          notes.items?.reduce((acc, link) => {
            if (
              link.note_1 &&
              !link.note_1.deleted &&
              link.noteId_1 !== this.id &&
              (link?.note_1?.title || '')?.toLowerCase()?.includes(search.toLowerCase())
            ) {
              acc.push(link.note_1);
            }

            return acc;
          }, [] as Note.Model[])
        )
      );
  }

  private getDocuments$(claim: Claim.Model, search: string) {
    return this.linkSvc
      .list$(
        { id: claim.id, objectType: Link.ObjectType.CLAIM },
        {
          owner: { id: claim.id, objectType: Link.ObjectType.CLAIM },
          linkType: Link.ObjectType.DOCUMENT,
          limit: 100,
        }
      )
      .pipe(
        map(documents =>
          documents.items?.reduce((acc, link) => {
            if (
              link.document_1 &&
              !link.document_1.deleted &&
              link.documentId_1 !== this.id &&
              link.document_1.title?.toLowerCase()?.includes(search.toLowerCase())
            ) {
              acc.push(link.document_1);
            }

            return acc;
          }, [] as Documents.Model[])
        )
      );
  }

  private getPayments$(claim: Claim.Model, search: string) {
    return this.linkSvc
      .list$(
        { id: claim.id, objectType: Link.ObjectType.CLAIM },
        {
          owner: { id: claim.id, objectType: Link.ObjectType.CLAIM },
          linkType: Link.ObjectType.PAYMENT,
          limit: 100,
        }
      )
      .pipe(
        map(payments =>
          payments.items?.reduce((acc, link) => {
            if (
              link.payment_1 &&
              !link.payment_1.deleted &&
              link.paymentId_1 !== this.id &&
              link.payment_1.title?.toLowerCase()?.includes(search.toLowerCase())
            ) {
              acc.push(link.payment_1);
            }

            return acc;
          }, [] as Claim.ClaimPayment.Model[])
        )
      );
  }

  ngOnDestroy(): void {
    this.value$.complete();
    this.search$.complete();
    this.refresh$.complete();
    this.loadingSubject.complete();
  }
}
